import { getCookie } from "@/utils/cookieUtils";

export const DEBUG_FEATURE_KEY = "debug";
export const DEBUG_FEATURE_EXPIRES_AT_KEY = "debug_expires_at";
export const DEBUG_FEATURE_TTL_MS = 60 * 60 * 1000;

function truthyFlag(value) {
  return ["1", "true", "yes", "on"].includes(String(value || "").toLowerCase());
}

function setLocalStorageDebugExpiresAt(now = Date.now()) {
  const expiresAt = now + DEBUG_FEATURE_TTL_MS;
  window.localStorage.setItem(DEBUG_FEATURE_EXPIRES_AT_KEY, String(expiresAt));

  return expiresAt;
}

function localStorageExpiresAt(now = Date.now()) {
  const value = window.localStorage.getItem(DEBUG_FEATURE_EXPIRES_AT_KEY);
  const expiresAt = Number(value);

  if (Number.isFinite(expiresAt) && expiresAt > 0) return expiresAt;

  return setLocalStorageDebugExpiresAt(now);
}

function localStorageFlag(now = Date.now()) {
  try {
    if (!truthyFlag(window.localStorage.getItem(DEBUG_FEATURE_KEY))) {
      return false;
    }

    const expiresAt = localStorageExpiresAt(now);

    if (expiresAt > now) return true;

    disableDebugFeature();
    return false;
  } catch (error) {
    return false;
  }
}

function cookieFlag() {
  try {
    return truthyFlag(getCookie(DEBUG_FEATURE_KEY));
  } catch (error) {
    return false;
  }
}

function queryFlag() {
  try {
    return truthyFlag(
      new URLSearchParams(window.location.search).get(DEBUG_FEATURE_KEY)
    );
  } catch (error) {
    return false;
  }
}

function debugSourceLabel({ queryEnabled, cookieEnabled, localStorageEnabled }) {
  if (queryEnabled) return "query";
  if (cookieEnabled) return "cookie";
  if (localStorageEnabled) return "localStorage";

  return "нет";
}

function autoDisableLabel({ sourceLabel, expiresAt }) {
  if (expiresAt) return new Date(expiresAt).toLocaleString("ru-RU");
  if (sourceLabel === "query") return "только пока открыт URL с debug=true";
  if (sourceLabel === "cookie") return "не задано для cookie";

  return "нет";
}

function autoDisableRemainingLabel({ sourceLabel, remainingMs }) {
  if (remainingMs !== null) {
    return `${Math.max(0, Math.ceil(remainingMs / 60000))} мин`;
  }

  if (sourceLabel === "query") return "не сохраняется";
  if (sourceLabel === "cookie") return "неизвестно";

  return "нет";
}

export function isDebugFeatureEnabled() {
  return queryFlag() || cookieFlag() || localStorageFlag();
}

/**
 * Включает debug в localStorage на ограниченное время.
 */
export function enableDebugFeature(now = Date.now()) {
  window.localStorage.setItem(DEBUG_FEATURE_KEY, "true");
  setLocalStorageDebugExpiresAt(now);
}

/**
 * Убирает debug из localStorage и cookie, чтобы флаг не оставался у клиента.
 */
export function disableDebugFeature() {
  window.localStorage.removeItem(DEBUG_FEATURE_KEY);
  window.localStorage.removeItem(DEBUG_FEATURE_EXPIRES_AT_KEY);
  document.cookie = `${DEBUG_FEATURE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

/**
 * Возвращает диагностическую информацию о debug-флаге без чтения компонентов из storage.
 */
export function getDebugFeatureInfo(now = Date.now()) {
  const queryEnabled = queryFlag();
  const cookieEnabled = cookieFlag();
  const localStorageEnabled = localStorageFlag(now);
  const enabled = queryEnabled || cookieEnabled || localStorageEnabled;
  const expiresAtValue = window.localStorage.getItem(
    DEBUG_FEATURE_EXPIRES_AT_KEY
  );
  const expiresAt = localStorageEnabled ? Number(expiresAtValue) : null;
  const remainingMs = expiresAt ? expiresAt - now : null;
  const sourceLabel = debugSourceLabel({
    queryEnabled,
    cookieEnabled,
    localStorageEnabled,
  });

  return {
    enabled,
    queryEnabled,
    cookieEnabled,
    localStorageEnabled,
    localStorageExpiresAt: expiresAt ? new Date(expiresAt) : null,
    autoDisableLabel: autoDisableLabel({ sourceLabel, expiresAt }),
    autoDisableRemainingLabel: autoDisableRemainingLabel({
      sourceLabel,
      remainingMs,
    }),
    sourceLabel,
    statusLabel: enabled ? "включён" : "выключен",
  };
}

/**
 * Показывает debug-автозаполнение ЭДО под общим флагом debug=true.
 */
export function isEdoDemoDataEnabled() {
  return isDebugFeatureEnabled();
}
